import React, { CSSProperties } from "react";
import { Box, SxProps, Typography } from "@mui/material";
import { Theme } from "@mui/system";

const unformattedCopy = (e: React.ClipboardEvent<HTMLDivElement>) => {
  // Get the selected content as a Range object
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  // Get the selected HTML content
  const range = selection.getRangeAt(0);
  const fragment = range.cloneContents();

  // Create a temporary element to hold the copied content
  const tempElement = document.createElement("div");
  tempElement.appendChild(fragment);

  // Remove background color and other unwanted styles from the copied content
  tempElement.querySelectorAll("*").forEach((el) => {
    const element = el as HTMLElement;
    element.style.fontWeight = "unset";
    element.style.color = "unset";
    element.style.backgroundColor = "unset";
  });

  if (!e.clipboardData) return;

  // Set the modified content as the data to be copied
  e.clipboardData.setData("text/plain", tempElement.innerText);
  e.clipboardData.setData("text/html", tempElement.innerHTML);

  // Prevent the default copy action
  e.preventDefault();
};

interface Props {
  alignLeft: boolean;
  children: string | React.ReactNode;
  onClick?: () => void;
  loading?: boolean;
  sx?: SxProps<Theme>;
  bubbleSx?: CSSProperties;
}
export default ({
  alignLeft,
  children,
  onClick,
  loading = false,
  sx,
  bubbleSx,
}: Props) => {
  const LoadingIndicator = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "2px",
        position: "relative",
        minHeight: "20px",
      }}
    >
      <Box
        sx={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: alignLeft ? "#666" : "rgba(255,255,255,0.9)",
          animation: "typeWave 1.8s ease-in-out infinite",
          animationDelay: "0s",
          "@keyframes typeWave": {
            "0%, 60%, 100%": {
              transform: "translateY(0px) scale(1)",
              opacity: 0.4,
            },
            "30%": {
              transform: "translateY(-4px) scale(1.2)",
              opacity: 1,
            },
          },
        }}
      />
      <Box
        sx={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: alignLeft ? "#666" : "rgba(255,255,255,0.9)",
          animation: "typeWave 1.8s ease-in-out infinite",
          animationDelay: "0.2s",
          "@keyframes typeWave": {
            "0%, 60%, 100%": {
              transform: "translateY(0px) scale(1)",
              opacity: 0.4,
            },
            "30%": {
              transform: "translateY(-4px) scale(1.2)",
              opacity: 1,
            },
          },
        }}
      />
      <Box
        sx={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: alignLeft ? "#666" : "rgba(255,255,255,0.9)",
          animation: "typeWave 1.8s ease-in-out infinite",
          animationDelay: "0.4s",
          "@keyframes typeWave": {
            "0%, 60%, 100%": {
              transform: "translateY(0px) scale(1)",
              opacity: 0.4,
            },
            "30%": {
              transform: "translateY(-4px) scale(1.2)",
              opacity: 1,
            },
          },
        }}
      />
    </Box>
  );

  return (
    <div
      style={{
        position: "relative",
        ...(onClick ? { cursor: "pointer" } : {}),
      }}
      onClick={onClick}
    >
      <Box
        onCopy={unformattedCopy}
        sx={{
          padding: "10px 14px",
          width: "100%",
          fontWeight: "500",
          ...(alignLeft
            ? {
                backgroundColor: "#F2F4F7",
                borderRadius: "4px 16px 16px",
                border: "1px solid #EAEBEC",
              }
            : {
                borderRadius: "16px 4px 16px 16px",
                background: "#1C9191",
              }),
          ...(loading
            ? {
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "-100%",
                  width: "100%",
                  height: "100%",
                  background: alignLeft
                    ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)"
                    : "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                  animation: "shimmer 2s infinite",
                  "@keyframes shimmer": {
                    "0%": { left: "-100%" },
                    "100%": { left: "100%" },
                  },
                },
              }
            : {}),
          ...sx,
        }}
      >
        <Typography
          variant="body"
          sx={{
            wordBreak: "break-word",
            whiteSpace: "pre-line", // Lets messages use new lines.
            fontWeight: "500",
            margin: 0,
            "& p": { margin: 0 },
            ...(alignLeft
              ? { color: "#3D3D3D" }
              : {
                  color: "#FFF",
                  "& a, & .ProseMirror a": {
                    color: "#FFF !important",
                    textDecoration: "underline !important",
                    fontWeight: 600,
                  },
                }),
            ...(loading && typeof children === "string" && children
              ? {
                  opacity: 0.7,
                  animation: "contentPulse 2s ease-in-out infinite",
                  "@keyframes contentPulse": {
                    "0%, 100%": { opacity: 0.7 },
                    "50%": { opacity: 0.5 },
                  },
                }
              : {}),
            ...bubbleSx,
          }}
        >
          {loading &&
          (!children || (typeof children === "string" && !children.trim())) ? (
            <LoadingIndicator />
          ) : (
            children
          )}
        </Typography>
      </Box>
    </div>
  );
};
